/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import appSettings from "appsettings.json";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

import { useMaterialUIController, setLayout } from "context";
import AppLoader from "components/AppLoader";
import CommonFooter from "components/CommonFooter";
import CommonStoreHeader from "components/CommonStoreHeader";

function MobileSignIn() {
  const recaptchaSiteKey = appSettings?.security?.recaptchaSiteKey || "";
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState("login");
  const [fullName, setFullName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [recaptchaOpen, setRecaptchaOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    if (!recaptchaOpen || !recaptchaSiteKey) return undefined;

    const existingScript = document.querySelector('script[src*="google.com/recaptcha/api.js"]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const widgetId = "mobile-signin-recaptcha";
    const renderCaptcha = () => {
      const container = document.getElementById(widgetId);
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (!container || !captchaApi || typeof captchaApi.render !== "function") return;
      if (container.getAttribute("data-rendered") === "true") return;

      captchaApi.render(widgetId, {
        sitekey: recaptchaSiteKey,
        callback: (token) => {
          setRecaptchaToken(token || "");
        },
        "expired-callback": () => {
          setRecaptchaToken("");
        },
      });
      container.setAttribute("data-rendered", "true");
    };

    const timer = window.setInterval(() => {
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (captchaApi && typeof captchaApi.render === "function") {
        renderCaptcha();
        window.clearInterval(timer);
      }
    }, 180);

    return () => window.clearInterval(timer);
  }, [recaptchaOpen, recaptchaSiteKey]);

  const sanitizedMobile = useMemo(() => mobile.replace(/\D/g, ""), [mobile]);
  const isMobileValid = sanitizedMobile.length >= 9 && sanitizedMobile.length <= 12;
  const isOtpValid = otp.trim().length > 0;
  const isSignUpFormValid =
    fullName.trim().length > 0 && billingAddress.trim().length > 0 && isMobileValid;

  const resetStateForMode = (mode) => {
    setAuthMode(mode);
    setFullName("");
    setBillingAddress("");
    setMobile("");
    setOtp("");
    setOtpStep(false);
    setError("");
    setSuccess("");
  };

  const requiresRecaptcha = (mode, isOtpStep) =>
    (mode === "login" && !isOtpStep) || (mode === "signup" && !isOtpStep);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (requiresRecaptcha(authMode, otpStep) && !recaptchaToken) {
      if (!recaptchaSiteKey) {
        setError("reCAPTCHA site key is not configured.");
        return;
      }
      setPendingSubmit({ authMode, otpStep });
      setRecaptchaOpen(true);
      return;
    }

    if (authMode === "login" && !otpStep) {
      if (!isMobileValid) return;

      setLoading(true);
      try {
        const url = `${appSettings.baseUrl}${
          appSettings.endpoints.createOtp
        }?phoneNo=${encodeURIComponent(sanitizedMobile)}`;
        const response = await fetch(url, { method: "POST" });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "Failed to send OTP.");
        }

        setOtpStep(true);
        setSuccess("OTP sent to your mobile number.");
      } catch (requestOtpError) {
        setError(requestOtpError.message || "Failed to send OTP.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (authMode === "signup" && !otpStep) {
      if (!isSignUpFormValid) return;

      setLoading(true);
      try {
        const signUpUrl = `${appSettings.baseUrl}${
          appSettings.endpoints.onboardByer
        }?name=${encodeURIComponent(fullName)}&billingAddress=${encodeURIComponent(
          billingAddress
        )}&phoneNo=${encodeURIComponent(sanitizedMobile)}&orgId=1`;
        const response = await fetch(signUpUrl, { method: "POST" });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "Signup failed.");
        }

        setOtpStep(true);
        setSuccess("Account created. OTP sent to your mobile number.");
      } catch (signUpError) {
        setError(signUpError.message || "Signup failed.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isOtpValid) return;

    setLoading(true);
    try {
      if (authMode === "signup") {
        const activateUrl = `${appSettings.baseUrl}${
          appSettings.endpoints.onboardOtpActivation
        }?otp=${encodeURIComponent(otp)}&phoneNo=${encodeURIComponent(sanitizedMobile)}`;
        const activateResponse = await fetch(activateUrl, { method: "POST" });

        if (!activateResponse.ok) {
          const message = await activateResponse.text();
          throw new Error(message || "OTP validation failed.");
        }

        const activationData = await activateResponse.json();
        if (activationData?.token && activationData?.refreshToken) {
          localStorage.setItem("token", activationData.token);
          localStorage.setItem("refreshToken", activationData.refreshToken);
          const isTocAccepted = activationData?.isTocAccepted === true;
          localStorage.setItem("isTocAccepted", isTocAccepted ? "true" : "false");
          navigate(isTocAccepted ? "/" : "/authentication/terms-and-conditions");
          return;
        }
      }

      const verifyUrl = `${appSettings.baseUrl}${
        appSettings.endpoints.getTokenByOtp
      }?otp=${encodeURIComponent(otp)}&phoneNo=${encodeURIComponent(sanitizedMobile)}`;
      const response = await fetch(verifyUrl, { method: "POST" });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "OTP validation failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      const isTocAccepted = data?.isTocAccepted === true;
      localStorage.setItem("isTocAccepted", isTocAccepted ? "true" : "false");
      navigate(isTocAccepted ? "/" : "/authentication/terms-and-conditions");
    } catch (verifyError) {
      setError(verifyError.message || "OTP validation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaContinue = async () => {
    if (!pendingSubmit || !recaptchaToken) return;

    setRecaptchaOpen(false);
    setPendingSubmit(null);

    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        background:
          "linear-gradient(180deg, rgba(15, 23, 42, 0.03) 0%, rgba(255,255,255,0) 220px), #f8fafc",
      }}
    >
      <CommonStoreHeader />

      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 4 }, py: 5 }}>
        <Card
          sx={{
            maxWidth: 1180,
            mx: "auto",
            borderRadius: { xs: 2.2, md: 10 },
            border: "1px solid rgba(255,255,255,0.35)",
            boxShadow: "0 22px 40px rgba(2, 6, 23, 0.24)",
            overflow: "hidden",
            bgcolor: "#f6f7f8",
          }}
        >
          <Grid container>
            <Grid item xs={12} md={3.5}>
              <Box
                sx={{
                  bgcolor: "#4f5052",
                  color: "#ffffff",
                  height: "100%",
                  px: { xs: 2.2, md: 3.2 },
                  py: { xs: 2.4, md: 3.2 },
                  borderBottomRightRadius: { xs: 0, md: 68 },
                }}
              >
                <Typography sx={{ mt: 0.6, fontSize: "1.8rem", fontWeight: 800, lineHeight: 1.1 }}>
                  {authMode === "login" ? "Log In" : "Sign Up"}
                </Typography>
                <Typography sx={{ mt: 2.1, color: "#f1f5f9", fontWeight: 500, maxWidth: 250 }}>
                  {authMode === "login"
                    ? "Shopped with us before? Log in with your details."
                    : "Create your account and get started with mobile verification."}
                </Typography>
                <Typography sx={{ mt: 3.3, fontWeight: 700 }}>New member?</Typography>
                <Button
                  onClick={() => resetStateForMode(authMode === "login" ? "signup" : "login")}
                  sx={{
                    mt: 1,
                    bgcolor: "#b71c2a",
                    color: "#ffffff !important",
                    fontWeight: 800,
                    borderRadius: 8,
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    boxShadow: "0 8px 18px rgba(0,0,0,0.2)",
                    "&:hover": { bgcolor: "#991b1b" },
                  }}
                >
                  {authMode === "login" ? "Click here to register" : "Back to login"}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={8.5}>
              <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 } }}>
                <Stack spacing={1.1} sx={{ maxWidth: 760 }}>
                  {authMode === "signup" && (
                    <>
                      <Typography sx={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 700 }}>
                        Full Name
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Your name"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#cbd5e1" },
                            "&:hover fieldset": { borderColor: "#94a3b8" },
                            "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                          },
                        }}
                      />
                      <Typography sx={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 700 }}>
                        Billing Address
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Your billing address"
                        value={billingAddress}
                        onChange={(event) => setBillingAddress(event.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#cbd5e1" },
                            "&:hover fieldset": { borderColor: "#94a3b8" },
                            "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                          },
                        }}
                      />
                    </>
                  )}
                  <Typography sx={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 700 }}>
                    Mobile Number
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="07XXXXXXXX"
                    value={mobile}
                    onChange={(event) => setMobile(event.target.value)}
                    inputProps={{ maxLength: 12 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "#cbd5e1" },
                        "&:hover fieldset": { borderColor: "#94a3b8" },
                        "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                      },
                    }}
                  />
                  {!isMobileValid && mobile.length > 0 && (
                    <Typography sx={{ color: "#2E7D32", fontSize: "0.85rem" }}>
                      Enter a valid mobile number.
                    </Typography>
                  )}
                  {otpStep && (
                    <>
                      <Typography
                        sx={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 700, mt: 2 }}
                      >
                        OTP
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value)}
                        inputProps={{ maxLength: 8 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            bgcolor: "#f8fafc",
                            "& fieldset": { borderColor: "#cbd5e1" },
                            "&:hover fieldset": { borderColor: "#94a3b8" },
                            "&.Mui-focused fieldset": { borderColor: "#b71c2a" },
                          },
                        }}
                      />
                      <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                        OTP is valid for 1 minute.
                      </Typography>
                    </>
                  )}
                  {error && (
                    <Typography sx={{ color: "#dc2626", fontSize: "0.88rem", mt: 1 }}>
                      {error}
                    </Typography>
                  )}
                  {success && (
                    <Typography sx={{ color: "#15803d", fontSize: "0.88rem", mt: 1 }}>
                      {success}
                    </Typography>
                  )}
                </Stack>

                <Box sx={{ textAlign: "center", mt: 3.4 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      loading ||
                      (!otpStep
                        ? authMode === "login"
                          ? !isMobileValid
                          : !isSignUpFormValid
                        : !isOtpValid)
                    }
                    sx={{
                      minWidth: 180,
                      bgcolor: "#b71c2a",
                      color: "#ffffff !important",
                      borderRadius: 6,
                      boxShadow: "0 10px 18px rgba(15, 23, 42, 0.18)",
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      "&:hover": {
                        bgcolor: "#991b1b",
                        boxShadow: "0 10px 20px rgba(153, 27, 27, 0.3)",
                      },
                      "&.Mui-disabled": { bgcolor: "#d1d5db", color: "#6b7280" },
                    }}
                  >
                    {loading
                      ? "PLEASE WAIT..."
                      : otpStep
                      ? "VERIFY OTP"
                      : authMode === "login"
                      ? "SEND OTP"
                      : "SIGN UP"}
                  </Button>
                </Box>

                <Box sx={{ mt: 3.2, textAlign: "center" }}>
                  {authMode === "login" ? (
                    <>
                      <Typography sx={{ color: "#374151", display: "inline" }}>
                        Not signed up yet?
                      </Typography>{" "}
                      <Typography
                        component={Link}
                        to="#"
                        onClick={(event) => {
                          event.preventDefault();
                          resetStateForMode("signup");
                        }}
                        sx={{ color: "#b71c2a", textDecoration: "none", fontWeight: 700 }}
                      >
                        SIGN UP NOW
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ color: "#374151", display: "inline" }}>
                        Already have an account?
                      </Typography>{" "}
                      <Typography
                        component={Link}
                        to="#"
                        onClick={(event) => {
                          event.preventDefault();
                          resetStateForMode("login");
                        }}
                        sx={{ color: "#b71c2a", textDecoration: "none", fontWeight: 700 }}
                      >
                        LOGIN
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(1px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader size={40} color="#b71c2a" />
        </Box>
      )}

      <Dialog open={recaptchaOpen} onClose={() => setRecaptchaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Security Verification</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "#475569" }}>
            Please complete the security check to continue.
          </Typography>
          {recaptchaSiteKey ? (
            <Box
              id="mobile-signin-recaptcha"
              sx={{
                minHeight: 78,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          ) : (
            <Typography sx={{ color: "#dc2626", fontSize: "0.9rem" }}>
              Missing reCAPTCHA site key in appsettings.json (security.recaptchaSiteKey).
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRecaptchaOpen(false);
              setPendingSubmit(null);
            }}
            sx={{
              minWidth: 108,
              borderColor: "#43a047",
              color: "#2e7d32",
              fontWeight: 700,
              "&:hover": {
                borderColor: "#2e7d32",
                bgcolor: "#e8f5e9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!recaptchaToken}
            onClick={handleRecaptchaContinue}
            sx={{
              minWidth: 108,
              bgcolor: "#b71c2a",
              color: "#ffffff !important",
              fontWeight: 700,
              "&:hover": { bgcolor: "#991b1b" },
              "&.Mui-disabled": {
                bgcolor: "#d1d5db",
                color: "#6b7280 !important",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <CommonFooter />
    </Box>
  );
}

export default MobileSignIn;
